import { BusinesscaseRevenue } from '@/types/businesscase';
import { ZymplerImportData, ZymplerRow, ZymplerValidationResult } from '@/types/businesscase';

// Column mapping from Zympler export to database fields
export const ZYMPLER_COLUMN_MAPPING: Record<string, string> = {
  // Dutch variants
  'Jaar': 'year',
  'Grid Connection Savings': 'grid_connection_savings',
  'Grid Purchase Costs Savings': 'grid_purchase_cost_savings',
  'Grid Purchase Cost Savings': 'grid_purchase_cost_savings',
  'Dayahead Incomes': 'dayahead_incomes',
  "HBE's": 'hbe_incomes',
  'HBEs': 'hbe_incomes',
  'Energy Taxes Savings': 'energy_tax_savings',
  'Energy Tax Savings': 'energy_tax_savings',
  'Endex Costs Savings': 'endex_cost_savings',
  'Imbalance Income': 'imbalance_income',
  'Imbalance Discount': 'imbalance_discount',
  'EMS Costs': 'ems_costs',
  'Total Opex': 'total_opex',
  'Total OPEX': 'total_opex',
  'Generator Costs Savings': 'generator_cost_savings',
  'Extra SDE': 'extra_sde',
  'Total Solar Revenues': 'total_solar_revenues',
  
  // English variants
  'Year': 'year',
  'Grid Connection': 'grid_connection_savings',
  'Grid Purchase': 'grid_purchase_cost_savings',
  'Dayahead': 'dayahead_incomes',
  'HBE': 'hbe_incomes',
  'Energy Tax': 'energy_tax_savings',
  'Endex': 'endex_cost_savings',
  'Generator': 'generator_cost_savings',
  'Solar': 'total_solar_revenues',
};

/**
 * Detect if the data uses period or comma as decimal separator
 */
export function detectDecimalSeparator(data: any[]): 'comma' | 'period' {
  let commaAsDecimalCount = 0;
  let periodAsDecimalCount = 0;
  
  // Sample first 100 rows
  const sample = data.slice(0, Math.min(100, data.length));
  
  for (const row of sample) {
    for (const value of Object.values(row)) {
      if (typeof value === 'string') {
        const trimmed = value.trim().replace(/^-/, ''); // Strip minus sign
        
        // European: "1.234,56" → comma is decimal (max 2 digits after comma)
        if (/,\d{1,2}$/.test(trimmed)) {
          commaAsDecimalCount++;
        }
        
        // US/Zympler: "1234.5678" → period is decimal
        if (/\.\d+$/.test(trimmed) && !/,/.test(trimmed)) {
          periodAsDecimalCount++;
        }
      }
    }
  }
  
  console.log('🔍 Decimal detection:', {
    commaAsDecimal: commaAsDecimalCount,
    periodAsDecimal: periodAsDecimalCount,
    format: commaAsDecimalCount > periodAsDecimalCount ? 'European (,)' : 'US/Zympler (.)'
  });
  
  return commaAsDecimalCount > periodAsDecimalCount ? 'comma' : 'period';
}

/**
 * Normalize a single numeric value with per-value decimal detection
 * Handles: -4302.333247040319, 26.820,19299, 1,234.56, €1.234,56
 */
function normalizePerValue(value: string): number {
  if (!value || value === '') return 0;
  
  // Trim, preserve minus sign, remove currency symbols and non-breaking spaces
  let s = String(value)
    .trim()
    .replace(/[\u00A0\u202F]/g, ' ') // Replace NBSP
    .replace(/[€$£¥]/g, '') // Remove currency
    .trim();
  
  const isNegative = s.startsWith('-');
  if (isNegative) s = s.substring(1).trim();
  
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  
  // Case 1: Both comma AND dot present → last one is decimal separator
  if (hasComma && hasDot) {
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    
    if (lastComma > lastDot) {
      // "26.820,19299" → comma is decimal
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      // "-4,302.333247040319" → dot is decimal
      s = s.replace(/,/g, '');
    }
  }
  // Case 2: Only comma present
  else if (hasComma) {
    // Check if it's decimal (e.g., "1234,56") or thousands (e.g., "1,234")
    if (/,\d{1,2}$/.test(s)) {
      // Comma at end with 1-2 digits → decimal separator
      s = s.replace(',', '.');
    } else if (/^\d{1,3}(,\d{3})+$/.test(s)) {
      // Pattern like "1,234" or "1,234,567" → thousands separator
      s = s.replace(/,/g, '');
    } else {
      // Default: treat comma as decimal
      s = s.replace(',', '.');
    }
  }
  // Case 3: Only dot present
  else if (hasDot) {
    // Check if it's decimal (e.g., "1234.56") or thousands (e.g., "1.234")
    if (/\.\d{1,}$/.test(s) && !/^\d{1,3}(\.\d{3})+$/.test(s)) {
      // Dot with digits after, not a thousands pattern → decimal separator
      // Keep as is
    } else if (/^\d{1,3}(\.\d{3})+$/.test(s)) {
      // Pattern like "1.234" or "1.234.567" → thousands separator
      s = s.replace(/\./g, '');
    }
    // else: keep as is
  }
  
  const num = parseFloat(s);
  const result = isNaN(num) ? 0 : (isNegative ? -num : num);
  
  return result;
}

/**
 * Normalize numeric value from string to number
 * Now uses per-value detection instead of file-wide separator
 */
export function normalizeNumericValue(value: any, decimalSeparator: 'comma' | 'period'): number {
  if (typeof value === 'number') return value;
  if (!value || value === '') return 0;
  
  // Use per-value normalization (decimalSeparator kept for backward compatibility/logging)
  return normalizePerValue(String(value));
}

/**
 * Map Zympler column headers to database field names
 */
export function mapZymplerColumns(headers: string[]): {
  mapped: Record<string, string>;
  unmapped: string[];
} {
  const mapped: Record<string, string> = {};
  const unmapped: string[] = [];
  
  for (const header of headers) {
    const normalized = header.trim();
    
    // Try exact match first
    let dbField = ZYMPLER_COLUMN_MAPPING[normalized];
    
    // Try case-insensitive match
    if (!dbField) {
      const lowerHeader = normalized.toLowerCase();
      for (const [key, value] of Object.entries(ZYMPLER_COLUMN_MAPPING)) {
        if (key.toLowerCase() === lowerHeader) {
          dbField = value;
          break;
        }
      }
    }
    
    if (dbField) {
      mapped[header] = dbField;
    } else if (normalized.toLowerCase() !== 'jaar' && normalized.toLowerCase() !== 'year') {
      unmapped.push(header);
    }
  }
  
  return { mapped, unmapped };
}

/**
 * Validate a single row of Zympler data
 */
export function validateZymplerRow(row: ZymplerRow, index: number): string[] {
  const warnings: string[] = [];
  
  if (!row.year || row.year < 2020 || row.year > 2100) {
    warnings.push(`Rij ${index + 1}: Ongeldige jaar waarde (${row.year})`);
  }
  
  // Check if all revenue values are zero
  const allZero = Object.values(row.mapped).every(v => v === 0);
  if (allZero) {
    warnings.push(`Rij ${index + 1}: Alle waarden zijn 0`);
  }
  
  return warnings;
}

/**
 * Parse Zympler file data
 */
export async function parseZymplerFile(
  jsonData: any[],
  existingRevenues: BusinesscaseRevenue[]
): Promise<ZymplerImportData> {
  if (!jsonData || jsonData.length === 0) {
    throw new Error('Geen data gevonden in bestand');
  }
  
  const headers = Object.keys(jsonData[0]);
  const { mapped, unmapped } = mapZymplerColumns(headers);
  
  // Detect decimal separator
  const decimalSeparator = detectDecimalSeparator(jsonData);
  
  const parsedData: ZymplerRow[] = [];
  const globalWarnings: string[] = [];
  
  for (let i = 0; i < jsonData.length; i++) {
    const row = jsonData[i];
    
    // Extract year
    let year = 0;
    for (const header of headers) {
      const field = mapped[header];
      if (field === 'year') {
        year = normalizeNumericValue(row[header], decimalSeparator);
        break;
      }
    }
    
    if (!year) {
      globalWarnings.push(`Rij ${i + 1}: Geen jaar gevonden, overgeslagen`);
      continue;
    }
    
    // Build mapped data
    const mappedValues: any = {};
    
    for (const header of headers) {
      const field = mapped[header];
      if (field && field !== 'year') {
        mappedValues[field] = normalizeNumericValue(
          row[header],
          decimalSeparator
        );
      }
    }
    
    const zymplerRow: ZymplerRow = {
      year,
      mapped: mappedValues,
      warnings: validateZymplerRow({ year, mapped: mappedValues }, i),
    };
    
    parsedData.push(zymplerRow);
  }
  
  return {
    fileName: '',
    uploadedAt: new Date().toISOString(),
    totalRows: parsedData.length,
    mappedColumns: Object.keys(mapped).filter(k => mapped[k] !== 'year'),
    unmappedColumns: unmapped,
    warnings: globalWarnings,
    parsedData,
  };
}

/**
 * Validate all parsed data
 */
export function validateZymplerData(data: ZymplerRow[]): ZymplerValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  if (data.length === 0) {
    errors.push('Geen data om te importeren');
  }
  
  // Check for duplicate years
  const years = data.map(d => d.year);
  const duplicates = years.filter((y, i) => years.indexOf(y) !== i);
  if (duplicates.length > 0) {
    errors.push(`Dubbele jaren gevonden: ${[...new Set(duplicates)].join(', ')}`);
  }
  
  // Collect all row warnings
  for (const row of data) {
    if (row.warnings && row.warnings.length > 0) {
      warnings.push(...row.warnings);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}
